import React, { useState, useRef, useEffect } from 'react';
import styles from './AccentDetector.module.css';

interface AccentResult {
  accent: string;
  confidence: number;
  features: string[];
  accentScores?: { [key: string]: number };
}

interface WordAlignment {
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  syllables: number;
  syllable_duration_ms: number;
}

interface RhythmAnalysis {
  total_duration_ms: number;
  total_syllables: number;
  speech_rate_syl_per_sec: number;
  timing_type: string;
  timing_variability: number;
  mean_pause_ms: number;
  pauses_detected: number;
  syllable_duration_variance: number;
}

interface PhoneticDeviation {
  word: string;
  expected: string;
  deviation: string;
  confidence: number;
}

interface ProcessingMetadata {
  alignment_success: boolean;
  phonetic_deviations_detected: number;
  method: string;
  total_processing_time_ms: number;
}

interface DetectionResponse {
  basicAnalysis: AccentResult;
  hfClassification?: { 
    accent?: string; 
    confidence?: number; 
    accentScores?: { [key: string]: number };
    result_type?: string;
    transcription?: string;
    model?: string;
    success?: boolean;
    diagnostic_info?: object;
  };
  detailedAnalysis?: string | object;
  wordAlignments?: { [key: string]: WordAlignment };
  rhythmAnalysis?: RhythmAnalysis;
  phoneticDeviations?: PhoneticDeviation[];
  success: boolean;
  error?: string;
  processing_metadata?: ProcessingMetadata;
}

const AccentDetector: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [result, setResult] = useState<AccentResult | null>(null);
  const [hfClassification, setHfClassification] = useState<DetectionResponse['hfClassification'] | null>(null);
  const [detailedAnalysis, setDetailedAnalysis] = useState<DetectionResponse['detailedAnalysis']>('');
  const [wordAlignments, setWordAlignments] = useState<{ [key: string]: WordAlignment } | null>(null);
  const [rhythmAnalysis, setRhythmAnalysis] = useState<RhythmAnalysis | null>(null);
  const [phoneticDeviations, setPhoneticDeviations] = useState<PhoneticDeviation[] | null>(null);
  const [processingMetadata, setProcessingMetadata] = useState<ProcessingMetadata | null>(null);
  const [error, setError] = useState('');
  const [shouldStartDrawing, setShouldStartDrawing] = useState(false);

  const PREDEFINED_PHRASE = "When the sunlight strikes raindrops in the air, they act as a prism and form a rainbow. The rainbow is a division of white light into many beautiful colors. These take the shape of a long round arch, with its path high above, and its two ends apparently beyond the horizon";

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // When canvas should start drawing, trigger the animation loop
  useEffect(() => {
    if (shouldStartDrawing && canvasRef.current && analyzerRef.current) {
      console.log('✅ Canvas ready, starting waveform animation');
      drawWaveform();
      setShouldStartDrawing(false);
    }
  }, [shouldStartDrawing, isRecording]);

  const startRecording = async () => {
    try {
      setError('');
      setResult(null);
      setDetailedAnalysis('');
      setRecordingTime(0);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Setup WebAudio for waveform visualization
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyzerRef.current = analyser;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        
        // Process audio directly for accent detection
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorderRef.current!.mimeType || 'audio/webm' 
        });
        processAudioForAccent(audioBlob);
      };

      mediaRecorder.start();
      
      // Set recording state FIRST so canvas renders to DOM
      setIsRecording(true);
      
      // Signal that canvas should start drawing (useEffect will handle it)
      setShouldStartDrawing(true);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err: any) {
      setError('Microphone access denied. Please enable microphone permissions.');
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const analyser = analyzerRef.current;
    
    if (!canvas || !analyser) {
      console.warn('Canvas or analyser not available');
      return;
    }

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) {
      console.warn('Canvas context not available');
      return;
    }

    // Clear canvas
    canvasCtx.fillStyle = '#F3F4F6';
    canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid/border
    canvasCtx.strokeStyle = '#E5E7EB';
    canvasCtx.lineWidth = 1;
    canvasCtx.strokeRect(0, 0, canvas.width, canvas.height);

    // Draw waveform
    canvasCtx.lineWidth = 2;
    canvasCtx.strokeStyle = '#6366F1';
    canvasCtx.beginPath();

    const sliceWidth = (canvas.width * 1.0) / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = canvas.height / 2 - (v * canvas.height) / 2;

      if (i === 0) {
        canvasCtx.moveTo(x, y);
      } else {
        canvasCtx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    canvasCtx.lineTo(canvas.width, canvas.height / 2);
    canvasCtx.stroke();

    // Continue animation if still recording
    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Stop the stream tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      mediaRecorderRef.current.stop();
      setError('');
    }
  };

  const processAudioForAccent = async (audioBlob: Blob) => {
    setIsProcessing(true);
    setError('');

    try {
      console.log('Processing audio for accent detection...');
      
      // Convert blob to base64
      const audioBase64 = await blobToBase64(audioBlob);
      
      // Use Railway API for testing purposes (local backend has HF DNS issues)
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      // const apiUrl = isDevelopment 
      //   ? '/api/extract-accent-from-audio'
      //   : 'https://astrostarapi-production.up.railway.app/extract-accent-from-audio';
      const apiUrl = 'https://p01--astrostar-api--j9tk4rgqc56r.code.run/extract-accent-from-audio';
    //  const apiUrl = '/api/extract-accent-from-audio';
      
      console.log(`Using API endpoint: ${apiUrl} (${isDevelopment ? 'development' : 'production'})`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          audioBase64,
          phrase: PREDEFINED_PHRASE 
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('API error:', response.status, errorData);
        throw new Error(`Failed to process audio (${response.status})`);
      }

      const data: DetectionResponse = await response.json();
      
      if (data.success) {
        setResult(data.basicAnalysis);
        setHfClassification(data.hfClassification || null);
        setDetailedAnalysis(data.detailedAnalysis);
        setWordAlignments(data.wordAlignments || null);
        setRhythmAnalysis(data.rhythmAnalysis || null);
        setPhoneticDeviations(data.phoneticDeviations || null);
        setProcessingMetadata(data.processing_metadata || null);
        console.log('✅ Accent detection successful:', data.basicAnalysis);
        console.log('✅ HF Classification:', data.hfClassification);
      } else {
        setError(data.error || 'Failed to detect accent');
        console.error('Detection failed:', data.error);
      }
    } catch (err: any) {
      setError(err.message || 'Error processing audio. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.accentDetector}>
      <div className={styles.detectorContainer}>
        <h1 className={styles.heading}>🎤 English Accent Detector</h1>
        <p className={styles.subtitle}>Pronounce the phrase below and get personalized accent feedback</p>

        {/* Phrase to Pronounce */}
        {!result && (
          <div className={styles.phraseBox}>
            <p className={styles.phraseLabel}>📝 Phrase to pronounce:</p>
            <p className={styles.phraseText}>"{PREDEFINED_PHRASE}"</p>
          </div>
        )}

        {/* Waveform Visualization */}
        {isRecording && (
          <div className={styles.waveformContainer}>
            <canvas 
              ref={canvasRef}
              className={styles.waveformCanvas}
              width={400}
              height={100}
            />
          </div>
        )}

        {/* Recording Section */}
        <div className={styles.recordingSection}>
          <div className={styles.recordingControls}>
            {!isRecording ? (
              <button
                className={`${styles.btnPrimary} ${styles.btnLarge}`}
                onClick={startRecording}
                disabled={isProcessing}
              >
                🎙️ Start Recording
              </button>
            ) : (
              <>
                <button
                  className={`${styles.btnDanger} ${styles.btnLarge}`}
                  onClick={stopRecording}
                >
                  ⏹️ Stop Recording
                </button>
                <div className={styles.recordingIndicator}>
                  <span className={styles.recordingDot}></span>
                  <span className={styles.recordingTime}>{formatTime(recordingTime)}</span>
                </div>
              </>
            )}
          </div>

          {/* Processing State */}
          {isProcessing && (
            <div className={styles.processing}>
              <div className={styles.spinner}></div>
              <p>Analyzing accent...</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <strong>⚠️ Error:</strong> {error}
            </div>
          )}
        </div>

        {/* Results Section */}
        {result && (
          <div className={styles.resultsSection}>
            <div className={styles.accentCard}>
              <h2>Detected Accent</h2>
              <div className={styles.accentInfo}>
                <div className={styles.accentName}>{result.accent}</div>
                <div className={styles.confidenceBar}>
                  <div
                    className={styles.confidenceFill}
                    style={{ width: `${result.confidence}%` }}
                  ></div>
                </div>
                <div className={styles.confidenceText}>Confidence: {result.confidence}%</div>
              </div>
            </div>

            {/* Accent Breakdown - Top 3 Only */}
            {result.accentScores && Object.keys(result.accentScores).length > 0 && (
              <div className={styles.breakdownCard}>
                <h3>📊 Top Accent Matches</h3>
                <div className={styles.breakdownList}>
                  {Object.entries(result.accentScores)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([accent, score], idx) => (
                      <div key={idx} className={styles.breakdownItem}>
                        <div className={styles.breakdownLabel}>
                          {idx === 0 ? '🏆' : idx === 1 ? '🥈' : '🥉'} {accent}
                        </div>
                        <div className={styles.breakdownBar}>
                          <div
                            className={styles.breakdownFill}
                            style={{ 
                              width: `${score}%`,
                              background: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : '#CD7F32'
                            }}
                          ></div>
                        </div>
                        <div className={styles.breakdownScore}>{score}%</div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* HuggingFace Classification Section */}
            {hfClassification && (
              <div className={styles.hfResultCard}>
                <h2>🤖 HuggingFace Result (for comparison)</h2>
                {hfClassification.result_type === 'transcription' ? (
                  <div className={styles.accentInfo}>
                    <p style={{ marginBottom: '10px', color: '#555', fontWeight: '600', fontSize: '1em' }}>
                      Transcription (Whisper model)
                    </p>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '8px',
                      fontStyle: 'italic',
                      color: '#1565c0',
                      fontSize: '1.05em',
                      lineHeight: '1.6',
                      border: '1px solid #90caf9'
                    }}>
                      "{hfClassification.transcription || 'No transcription available'}"
                    </div>
                    <p style={{ marginTop: '12px', fontSize: '0.9em', color: '#888' }}>
                      <em>Model: {hfClassification.model || 'Unknown'}</em>
                    </p>
                  </div>
                ) : (
                  <div className={styles.accentInfo}>
                    <div className={styles.accentName}>{hfClassification.accent || 'Unknown'}</div>
                    <div className={styles.confidenceBar}>
                      <div
                        className={styles.confidenceFill}
                        style={{ 
                          width: `${hfClassification.confidence || 0}%`,
                          background: '#9c27b0'
                        }}
                      ></div>
                    </div>
                    <div className={styles.confidenceText}>Confidence: {hfClassification.confidence || 0}%</div>
                  </div>
                )}
              </div>
            )}

            {/* HF Accent Breakdown - Top 3 Only */}
            {hfClassification?.accentScores && Object.keys(hfClassification.accentScores).length > 0 && (
              <div className={styles.breakdownCard}>
                <h3>📊 HF Top Accent Matches</h3>
                <div className={styles.breakdownList}>
                  {Object.entries(hfClassification.accentScores)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([accent, score], idx) => (
                      <div key={idx} className={styles.breakdownItem}>
                        <div className={styles.breakdownLabel}>
                          {idx === 0 ? '🏆' : idx === 1 ? '🥈' : '🥉'} {accent}
                        </div>
                        <div className={styles.breakdownBar}>
                          <div
                            className={styles.breakdownFill}
                            style={{ 
                              width: `${score}%`,
                              background: '#9c27b0'
                            }}
                          ></div>
                        </div>
                        <div className={styles.breakdownScore}>{score}%</div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Features */}
            <div className={styles.featuresCard}>
              <h3>✨ Key Observations</h3>
              <ul className={styles.featuresList}>
                {result.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>

            {/* Detailed Analysis */}
            {detailedAnalysis && (
              <div className={styles.analysisCard}>
                <h3>📖 Detailed Analysis</h3>
                <div className={styles.analysisText}>
                  {typeof detailedAnalysis === 'string' ? (
                    // String format: split by lines
                    detailedAnalysis.split('\n').map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))
                  ) : typeof detailedAnalysis === 'object' ? (
                    // Object format: pretty-print as JSON in code block
                    <div style={{ 
                      backgroundColor: '#1e1e1e', 
                      color: '#d4d4d4',
                      padding: '16px', 
                      borderRadius: '8px',
                      overflowX: 'auto',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      fontFamily: "'Fira Code', 'Courier New', monospace",
                      maxHeight: '400px',
                      overflowY: 'auto',
                      border: '1px solid #404040'
                    }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                        {JSON.stringify(detailedAnalysis, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <p>No detailed analysis available</p>
                  )}
                </div>
              </div>
            )}

            {/* Word Alignments Timeline - Wave Pattern Visualization */}
            {wordAlignments && Object.keys(wordAlignments).length > 0 && (
              <div className={styles.analysisCard}>
                <h3>⏱️ Word Alignment Timeline (ms)</h3>
                
                {/* Wave Pattern Animation */}
                <div style={{ marginTop: '12px' }}>
                  <div className={styles.waveTimeline}>
                    {Object.entries(wordAlignments)
                      .sort((a, b) => a[1].start_ms - b[1].start_ms)
                      .map(([word, alignment], idx) => (
                        <div 
                          key={idx} 
                          className={styles.waveWord}
                        >
                          {word} - {alignment.duration_ms}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Rhythm Analysis */}
            {rhythmAnalysis && (
              <div className={styles.analysisCard}>
                <h3>🎵 Speech Rhythm Analysis</h3>
                <div className={styles.rhythmGrid}>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Total Duration:</span>
                    <span className={styles.rhythmValue}>
                      {(rhythmAnalysis.total_duration_ms / 1000).toFixed(1)}s
                    </span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Syllables:</span>
                    <span className={styles.rhythmValue}>{rhythmAnalysis.total_syllables}</span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Speech Rate:</span>
                    <span className={styles.rhythmValue}>
                      {rhythmAnalysis.speech_rate_syl_per_sec.toFixed(1)} syllables/sec
                    </span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Timing Type:</span>
                    <span className={styles.rhythmValue}>{rhythmAnalysis.timing_type}</span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Timing Variability:</span>
                    <span className={styles.rhythmValue}>
                      {(rhythmAnalysis.timing_variability).toFixed(3)}
                    </span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Average Pause:</span>
                    <span className={styles.rhythmValue}>{rhythmAnalysis.mean_pause_ms.toFixed(1)}ms</span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Pauses Detected:</span>
                    <span className={styles.rhythmValue}>{rhythmAnalysis.pauses_detected}</span>
                  </div>
                  <div className={styles.rhythmItem}>
                    <span className={styles.rhythmLabel}>Syllable Variance:</span>
                    <span className={styles.rhythmValue}>
                      {(rhythmAnalysis.syllable_duration_variance).toFixed(2)}ms
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Phonetic Deviations */}
            {phoneticDeviations && phoneticDeviations.length > 0 && (
              <div className={styles.analysisCard}>
                <h3>🔊 Phonetic Deviations</h3>
                <div className={styles.deviationsList}>
                  {phoneticDeviations.map((dev, idx) => (
                    <div key={idx} className={styles.deviationItem}>
                      <div className={styles.deviationWord}>
                        <strong>{dev.word}</strong>
                      </div>
                      <div className={styles.deviationExpected}>
                        Expected: <code>{dev.expected}</code>
                      </div>
                      <div className={styles.deviationNote}>{dev.deviation}</div>
                      <div className={styles.deviationConfidence}>
                        <div className={styles.confidenceLabel}>
                          Confidence: {(dev.confidence * 100).toFixed(0)}%
                        </div>
                        <div className={styles.confidenceBar}>
                          <div
                            className={styles.confidenceFill}
                            style={{ width: `${dev.confidence * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Processing Metadata */}
            {processingMetadata && (
              <div className={styles.metadataCard}>
                <h4>⚙️ Processing Details</h4>
                <div className={styles.metadataGrid}>
                  <span>Method: {processingMetadata.method}</span>
                  <span>Time: {processingMetadata.total_processing_time_ms}ms</span>
                  <span>
                    Alignment: {processingMetadata.alignment_success ? '✅ Success' : '❌ Failed'}
                  </span>
                  <span>Deviations: {processingMetadata.phonetic_deviations_detected}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <button
                className={styles.btnPrimary}
                onClick={() => {
                  setResult(null);
                  setHfClassification(null);
                  setDetailedAnalysis('');
                  setWordAlignments(null);
                  setRhythmAnalysis(null);
                  setPhoneticDeviations(null);
                  setProcessingMetadata(null);
                  setRecordingTime(0);
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccentDetector;
